import type { Href } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

import { Fonts, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { homeContent, type Inline, type LinkTarget } from '@/lib/home-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';

import { ProseLink } from './prose-link';

// Evergreen SEO copy under the home-page carousels. Content + links live in
// src/lib/home-content.ts; this only renders the block list for the active
// locale, reusing the prose scale from portable-text-body.tsx (paragraph 16/26,
// h2 24, h3 20, h4 17, all headings serif) so it reads like the article pages.

/** Internal href to a catalogue hub or a city guide. */
function linkHref(locale: Locale, to: LinkTarget): Href {
  if (typeof to === 'object') {
    return { pathname: '/[lang]/[city]', params: { lang: locale, city: to.city } };
  }
  // `as Href`: typed routes can't narrow a runtime-chosen pathname union to one
  // route, but every hub takes a single `lang` param, so the cast is safe. Same
  // trick as components/riviera/nav-links.ts.
  return { pathname: `/[lang]/${to}`, params: { lang: locale } } as Href;
}

function InlineRuns({ runs, locale }: { runs: Inline[]; locale: Locale }) {
  return (
    <>
      {runs.map((run, i) => {
        if (typeof run === 'string') return run;
        if ('bold' in run) {
          return (
            <Text key={i} style={styles.strong}>
              {run.bold}
            </Text>
          );
        }
        // Renders a real <a href> on web (crawlable) and a Pressable-backed
        // Text on native — inline within the paragraph either way.
        return (
          <ProseLink key={i} href={linkHref(locale, run.link.to)}>
            {run.link.text}
          </ProseLink>
        );
      })}
    </>
  );
}

export function MainContent() {
  const theme = useTheme();
  const { locale } = useLocale();
  const blocks = homeContent[locale];

  return (
    <View style={[styles.outer, { borderTopColor: theme.border }]}>
      <View style={styles.column}>
        {blocks.map((block, i) => {
          if (block.type === 'heading') {
            const headingStyle =
              block.level === 2 ? styles.h2 : block.level === 3 ? styles.h3 : styles.h4;
            const blockStyle =
              block.level === 2 ? styles.h2Block : block.level === 3 ? styles.h3Block : styles.h4Block;
            return (
              <View key={i} style={blockStyle}>
                <Text
                  role="heading"
                  aria-level={block.level}
                  style={[headingStyle, { color: theme.text, fontFamily: Fonts?.serif }]}>
                  {block.text}
                </Text>
              </View>
            );
          }

          if (block.type === 'list') {
            return (
              <View key={i} style={styles.list}>
                {block.items.map((item, j) => (
                  <View key={j} style={styles.listItem}>
                    <Text style={[styles.listMarker, { color: theme.gold }]}>{'•'}</Text>
                    <Text style={[styles.paragraph, styles.listItemText, { color: theme.text }]}>
                      <InlineRuns runs={item} locale={locale} />
                    </Text>
                  </View>
                ))}
              </View>
            );
          }

          return (
            <View key={i} style={styles.paragraphBlock}>
              <Text style={[styles.paragraph, { color: theme.text }]}>
                <InlineRuns runs={block.runs} locale={locale} />
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outer: {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.five,
    marginTop: Spacing.two,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  column: {
    width: '100%',
    maxWidth: 760,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 26,
  },
  paragraphBlock: {
    marginBottom: Spacing.three,
  },
  h2: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 32,
  },
  h2Block: {
    marginBottom: Spacing.three,
  },
  h3: {
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 28,
  },
  h3Block: {
    marginTop: Spacing.three,
    marginBottom: Spacing.two,
  },
  h4: {
    fontSize: 17,
    fontWeight: '700',
    lineHeight: 24,
  },
  h4Block: {
    marginTop: Spacing.two,
    marginBottom: Spacing.one,
  },
  list: {
    marginBottom: Spacing.three,
    gap: Spacing.one,
  },
  listItem: {
    flexDirection: 'row',
    gap: Spacing.two,
  },
  listMarker: {
    fontSize: 16,
    lineHeight: 26,
  },
  listItemText: {
    flex: 1,
  },
  strong: {
    fontWeight: '700',
  },
});
